// mobile/hero.js — mobile site (ported from website2m-hero.jsx): floating nav + full-cover
// menu, and the hero — headline, liquid-glass feature pills, the phone deck (swipe between
// real mobile screens, neighbours peek). Screens come from ./screens.js.
// CTAs are wired to the app's real destinations (/login, Calendly) like the desktop page.
import React from 'react'
import { Link } from 'react-router-dom'
import { N2Ic } from '../icons'
import { N2Btn, N2Sticker, N2Toast, N2Fit } from '../shell'
import { N2Headline, N2Launch, N2LiquidDefs, N2ToastCycle } from '../hero'
import { n2Ambient } from '../ambient'
import { DEMO_URL } from '../sections'
import { trackBookDemoClick, trackGetStartedClick, trackLoginClick } from '../track'
import { MsCalendar, MsBookings, MsClients, MsMessages } from './screens'
import namiLogo from '../../../assets/nami-logo.png'

const N3_FEATURES = [
	{ id: 'calendar', icon: 'calendar', label: 'Calendar', dwell: 6400,
		screen: (on) => <MsCalendar on={on} />,
		float: () => (
			<React.Fragment>
				<div className="n2-fpos" style={{ right: 4, top: '8%', '--rot': '2deg' }}><N2ToastCycle /></div>
				<N2Sticker blush icon="heart" style={{ left: 6, top: '66%', transform: 'rotate(-4deg)' }}>Built with groomers</N2Sticker>
			</React.Fragment>
		) },
	{ id: 'bookings', icon: 'inbox', label: 'Bookings', dwell: 6600,
		screen: (on) => <MsBookings on={on} />,
		float: () => (
			<div className="n2-fpos" style={{ right: 2, top: '30%', '--rot': '2.5deg' }}>
				<N2Toast ic="lav" icon="bell" title="Approved in one tap" sub="confirmation & reminder — automatic" />
			</div>
		) },
	{ id: 'clients', icon: 'users', label: 'Clients & pets', dwell: 8400,
		screen: (on) => <MsClients on={on} />,
		float: () => (
			<React.Fragment>
				<N2Sticker icon="spark" style={{ left: 6, top: '6%', transform: 'rotate(-2.5deg)' }}>Every quirk, remembered</N2Sticker>
				<div className="n2-fpos" style={{ right: 2, top: '72%', '--rot': '2deg' }}>
					<N2Toast ic="blush" icon="heart" title="Nori · sensitive ears" sub="note surfaces at check-in" />
				</div>
			</React.Fragment>
		) },
	{ id: 'messages', icon: 'chat', label: 'Messages + AI', dwell: 9200,
		screen: (on) => <MsMessages on={on} />,
		float: () => (
			<React.Fragment>
				{/* pinned to the phone's top-right corner: the bezel sits a fixed ~24px below the
				    deck and the island line scales with the card (≈3cqw), so this stays on the
				    island row at every phone width instead of drifting onto the contact row */}
				<N2Sticker icon="spark" style={{ right: 6, top: 'calc(24px + 3cqw)', transform: 'rotate(2deg)' }}>One tap to book, mid-groom</N2Sticker>
				<div className="n2-fpos" style={{ left: 4, top: '52%', '--rot': '-2deg' }}>
					<N2Toast ic="lav" icon="calendar" title="Booked from a text" sub="Cooper · Sat 11:00 AM · zero back-and-forth" />
				</div>
			</React.Fragment>
		) }
]

/* ── nav: glass pill + full-cover menu ── */
const N3_LINKS = [['Product', '#top', '01'], ['Features', '#features', '02'], ['Pricing', '#pricing', '03'], ['FAQ', '#faq', '04']]
export function N3Nav({ cta, open, setOpen }) {
	const [scrolled, setScrolled] = React.useState(false)
	React.useEffect(() => {
		// The app scrolls on <body> (see index.css), so listen in the capture phase
		// and read whichever element scrolls.
		const top = () => window.scrollY || document.body.scrollTop || document.documentElement.scrollTop || 0
		const fn = () => setScrolled(top() > 24)
		fn()
		window.addEventListener('scroll', fn, { capture: true, passive: true })
		return () => window.removeEventListener('scroll', fn, { capture: true })
	}, [])
	React.useEffect(() => {
		// Lock scrolling while the menu covers the page — body is the scroller here.
		const els = [document.documentElement, document.body]
		els.forEach((el) => { el.style.overflow = open ? 'hidden' : '' })
		return () => els.forEach((el) => { el.style.overflow = '' })
	}, [open])
	return (
		<React.Fragment>
			<nav className={'n3-nav n3-fixed' + (scrolled ? ' is-scrolled' : '')}>
				<div className="n3-nav-in">
					<a className="n2-logo" href="#top" onClick={() => setOpen(false)}><img src={namiLogo} alt="Nami" /><span>Nami</span></a>
					<Link className="n3-nav-cta" to="/login" onClick={() => { trackGetStartedClick('mobile_nav', cta)(); setOpen(false) }}>{cta}</Link>
					<button type="button" className={'n3-burger' + (open ? ' open' : '')} aria-label={open ? 'Close menu' : 'Menu'} aria-expanded={open} onClick={() => setOpen(!open)}><i></i><i></i><i></i></button>
				</div>
			</nav>
			<div className={'n3-menu' + (open ? ' open' : '')} aria-hidden={!open}>
				<div className="n3-menu-scrim" onClick={() => setOpen(false)}></div>
				<div className="n3-menu-panel">
					<nav className="n3-menu-links" aria-label="Site">
						{N3_LINKS.map(([l, h, n]) => (
							<a className="n3-menu-link" href={h} key={l} onClick={() => setOpen(false)}>
								<em>{n}</em><span>{l}</span><N2Ic name="chevron" size={18} sw={2.2} />
							</a>
						))}
					</nav>
					<div className="n3-menu-row">
						<Link className="n3-menu-sec" to="/login" onClick={() => { trackLoginClick('mobile_menu')(); setOpen(false) }}><N2Ic name="users" size={16} sw={1.9} />Log in</Link>
						<a className="n3-menu-sec" href={DEMO_URL} target="_blank" rel="noopener noreferrer" onClick={trackBookDemoClick('mobile_menu')}><N2Ic name="calendar" size={16} sw={1.9} />Book a demo</a>
					</div>
					<div className="n3-menu-foot">
						<N2Btn to="/login" onClick={trackGetStartedClick('mobile_menu', cta)}>{cta}</N2Btn>
						<span className="n3-menu-note">Free for founding shops · every feature · no card</span>
					</div>
				</div>
			</div>
		</React.Fragment>
	)
}

/* ── hero ── */
export function N3Hero({ emph = 'glass', ink = 'gradient', ambient = 'both', autoPlay = true }) {
	const reduce = React.useRef(window.matchMedia('(prefers-reduced-motion: reduce)').matches).current
	const canvasRef = React.useRef(null), pillsRef = React.useRef(null), trackRef = React.useRef(null), deckRef = React.useRef(null)
	const [idx, setIdx] = React.useState(0)
	const idxRef = React.useRef(0)
	const [auto, setAuto] = React.useState(autoPlay && !reduce)
	const [run, setRun] = React.useState(0)
	const [visible, setVisible] = React.useState(true)
	const [lens, setLens] = React.useState(null)
	const progRef = React.useRef(false), progT = React.useRef(0)
	const n = N3_FEATURES.length
	React.useEffect(() => { setAuto(autoPlay && !reduce) }, [autoPlay, reduce])
	React.useEffect(() => { idxRef.current = idx }, [idx])

	React.useEffect(() => {
		if (!canvasRef.current) return
		const inst = n2Ambient(canvasRef.current, ambient)
		return () => inst && inst.stop()
	}, [ambient])

	// liquid lens follows the active pill; the rail keeps it centered
	const measure = React.useCallback(() => {
		const row = pillsRef.current
		if (!row) return
		const btn = row.querySelectorAll('.n2-htab')[idx]
		if (btn) setLens({ x: btn.offsetLeft, y: btn.offsetTop, w: btn.offsetWidth, h: btn.offsetHeight })
	}, [idx])
	React.useLayoutEffect(measure, [measure])
	React.useEffect(() => {
		window.addEventListener('resize', measure)
		if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure)
		return () => window.removeEventListener('resize', measure)
	}, [measure])
	React.useEffect(() => {
		const row = pillsRef.current
		if (!row) return
		const btn = row.querySelectorAll('.n2-htab')[idx]
		if (!btn) return
		row.scrollTo({ left: Math.max(0, btn.offsetLeft + btn.offsetWidth / 2 - row.clientWidth / 2), behavior: reduce ? 'auto' : 'smooth' })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [idx])

	const centerOf = (track, i) => { const card = track.children[i]; return card ? card.offsetLeft + card.offsetWidth / 2 - track.clientWidth / 2 : 0 }
	const go = React.useCallback((i, smooth = true) => {
		const track = trackRef.current
		if (!track) return
		progRef.current = true
		window.clearTimeout(progT.current)
		progT.current = window.setTimeout(() => { progRef.current = false }, 900)
		track.scrollTo({ left: centerOf(track, i), behavior: smooth && !reduce ? 'smooth' : 'auto' })
		setIdx(i)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// swipes: the card nearest the center becomes active; any touch stops auto-advance
	React.useEffect(() => {
		const track = trackRef.current
		if (!track) return
		let raf = 0
		const calc = () => {
			raf = 0
			const c = track.scrollLeft + track.clientWidth / 2
			let best = 0, bd = Infinity
			Array.from(track.children).forEach((el, i) => { const d = Math.abs(el.offsetLeft + el.offsetWidth / 2 - c); if (d < bd) { bd = d; best = i } })
			setIdx((cur) => (cur === best ? cur : best))
		}
		const onScroll = () => { if (!raf) raf = requestAnimationFrame(calc) }
		const stop = () => setAuto(false)
		track.addEventListener('scroll', onScroll, { passive: true })
		track.addEventListener('pointerdown', stop)
		track.addEventListener('touchstart', stop, { passive: true })
		track.addEventListener('wheel', stop, { passive: true })
		const ro = new ResizeObserver(() => { progRef.current = true; track.scrollTo({ left: centerOf(track, idxRef.current) }); window.setTimeout(() => { progRef.current = false }, 300) })
		ro.observe(track)
		return () => {
			track.removeEventListener('scroll', onScroll); track.removeEventListener('pointerdown', stop)
			track.removeEventListener('touchstart', stop); track.removeEventListener('wheel', stop)
			ro.disconnect(); if (raf) cancelAnimationFrame(raf)
		}
	}, [])

	// only advance while the deck is on screen
	React.useEffect(() => {
		const el = deckRef.current
		if (!el) return
		const io = new IntersectionObserver((es) => setVisible(es[0].isIntersecting), { threshold: 0.15 })
		io.observe(el)
		return () => io.disconnect()
	}, [])
	React.useEffect(() => {
		if (!auto || !visible) return
		const t = window.setTimeout(() => go((idxRef.current + 1) % n), N3_FEATURES[idx].dwell)
		return () => window.clearTimeout(t)
	}, [idx, auto, visible, go, run, n])

	// a pill tap jumps there and restarts the dwell cycle (progress fill included); swiping the deck still stops auto-advance
	const pick = (i) => { setAuto(autoPlay && !reduce); setRun((r) => r + 1); if (i !== idx) go(i) }

	return (
		<header className="n3-hero" id="top">
			<N2LiquidDefs />
			<div className="n2-mesh" aria-hidden="true"><i className="m1"></i><i className="m2"></i><i className="m3"></i><i className="m4"></i><i className="m5"></i><i className="m6"></i><i className="m7"></i><i className="m8"></i><i className="m9"></i></div>
			{ambient !== 'calm' ? <canvas key={ambient} className="n2-ambient" ref={canvasRef} aria-hidden="true"></canvas> : null}
			<div className="n2-grain" aria-hidden="true"></div>
			<div className="n3-hero-head">
				<N2Headline emph={emph} ink={ink} lines={3} />
				<p className="n2-hero-sub">A new, modern way to run your grooming business, with intelligent features that make your day run itself. <strong className="n2-sub-em">Free, with every feature unlocked.</strong></p>
				<N2Launch />
			</div>

			<div className="n3-pillsrow">
				<div className="n3-pills" ref={pillsRef} role="tablist" aria-label="Explore Nami">
					<div className="n3-pills-in">
						{lens ? <span className="n2-htab-lens" style={{ transform: 'translate(' + (lens.x - 5) + 'px,' + (lens.y - 3) + 'px)', width: lens.w + 10, height: lens.h + 6 }} aria-hidden="true"></span> : null}
						{N3_FEATURES.map((ft, i) => (
							<button key={ft.id} type="button" role="tab" aria-selected={i === idx} className={'n2-htab' + (i === idx ? ' on' : '')} onClick={() => pick(i)}>
								{auto && i === idx ? <i className="n2-htab-fill" key={'r' + idx + '-' + run} style={{ '--dur': ft.dwell + 'ms' }} aria-hidden="true"></i> : null}
								<N2Ic name={ft.icon} size={15} sw={2} />{ft.label}
							</button>
						))}
					</div>
				</div>
			</div>

			<div className="n3-deck" ref={deckRef}>
				<div className="n3-deck-glow" aria-hidden="true"></div>
				<div className="n3-track" ref={trackRef}>
					{N3_FEATURES.map((ft, i) => {
						const on = i === idx
						return (
							<div key={ft.id} className={'n3-card' + (on ? ' on' : '')}
								role={on ? undefined : 'button'} tabIndex={on ? undefined : 0} aria-label={on ? undefined : 'Show ' + ft.label}
								onClick={on ? undefined : () => pick(i)}
								onKeyDown={on ? undefined : (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(i) } }}>
								<div className="n3-phone">
									<div className="n3-bezel">
										<div className="n3-screen">
											<N2Fit w={402} h={874}>{ft.screen(on)}</N2Fit>
										</div>
									</div>
								</div>
								<span className="n3-ctag">{ft.label}</span>
							</div>
						)
					})}
				</div>
				<div className="n3-floats" aria-hidden="true">
					{N3_FEATURES.map((ft, i) => <div key={ft.id} className={'n3-float' + (i === idx ? ' on' : '')}>{ft.float()}</div>)}
				</div>
			</div>
		</header>
	)
}
